// @flow
import React from 'react'
import styled, { css } from 'styled-components'
import moment from 'moment'
import 'react-dates/initialize'
import {
  DayPickerSingleDateController,
  DayPickerRangeController,
} from 'react-dates'
import Range from 'rc-slider/lib/Range'
import { mediaMax } from 'frontend_core/dist/styles/media'
import { zIndexes } from 'frontend_core/dist/styles/variables'
import TextField from 'components/molecules/SearchTextField'
import SelectField from 'components/molecules/SearchSelectField'
import RangeSelectField from 'components/molecules/SearchRangeSelectField'
import CheckboxWrap from 'components/molecules/SearchCheckboxWrap'
import Colors from 'components/styles/colors'

const SearchContent = styled.div`
  ${mediaMax.desktop`
    width: 100%;
    padding: 20px 12px;
    z-index: ${zIndexes.topmost};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;
    background-color: ${Colors.white};
  `};
`

const Section = styled.div`
  margin-bottom: 8px;
`

const Body = styled.div`
  padding: 24px 0;
  border-bottom: ${props => (props.last ? 0 : '1px solid ' + Colors.grayD)};
`

const SideContentTitle = styled.p`
  color: #999;
  font-weight: bold;
`

const BodyContent = styled.div`
  margin-top: 24px;
`

const RangeBodyContent = styled.div`
  margin: 24px 10px 0;
  @include mq-max(sm) {
    margin-bottom: 32px;
  }
`

const CheckboxList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  margin-top: -24px;
`

const CheckboxListItem = styled.li`
  display: inline-block;
  margin-top: 24px;
  width: 50%;
  ${({ oneLiner }) =>
    oneLiner &&
    css`
      width: 100%;
    `};
`

const SlideInputWrapper = styled.div`
  width: 38%;
  @include mq-max(sm) {
    width: 41%;
  }
  cursor: text;
  float: left;
`

const SearchButtonWrapper = styled.div`
  display: inline-block;
  width: 50%;

  &:first-child {
    padding-right: 6px;
  }

  &:not(:first-child) {
    padding-left: 6px;
  }
`
const SearchButton = styled.button`
  font-size: 12px;
  display: inline-block;
  width: 100%;
  background: #4abfe6;
  padding: 10px;
  color: #fff;
  font-weight: bold;
  border: none;
  border-radius: 3px;
  outline: none;
  cursor: pointer;
  &:hover {
    background: #42a9cc;
  }
  @include mq-max(sm) {
    padding: 12px;
    font-size: 16px;
  }
`
const SearchButtonContainerSpSpace = styled.div`
  display: none;
  ${mediaMax.desktop`
    display: block;
    height: 62px;
  `};
`

const FilterLink = styled.a`
  display: block;
  padding: 8px;
  margin-top: 32px;
  text-align: center;
  color: ${Colors.white};
  font-size: 12px;
  font-weight: bold;
  border-radius: 3px;
  border-bottom: 1px solid ${Colors.blue};
  background-color: ${Colors.blue};
  ${mediaMax.desktop`
    display: none;
  `} &:hover {
    background-color: ${Colors.blueBdColor};
  }
`

const handleStyle = {
  marginLeft: -10,
  marginTop: -8,
  width: 20,
  height: 20,
  backgroundColor: Colors.blueBdColor,
  borderColor: Colors.blue,
  boxShadow: 'none',
}

const railStyle = {
  height: 2,
  backgroundColor: Colors.gray,
}
const trackStyle = {
  height: 2,
  backgroundColor: Colors.blue,
}
const handleUnspecifiedStyle = {
  marginLeft: -10,
  marginTop: -8,
  width: 20,
  height: 20,
  backgroundColor: Colors.gray,
  borderColor: Colors.gray,
  boxShadow: 'none',
}
const trackUnspecifiedStyle = {
  height: 2,
  backgroundColor: Colors.gray,
}

const CheckInOnClickWrapper = styled.div`
  position: absolute;
  display: inline-block;
  background-color: ${Colors.white};
  box-shadow: 0 6px 15px 0 rgba(0, 0, 0, 0.2);
  z-index: 1;

  &[data-type-width='fullWidth'] {
    width: 100%;
  }
`

const ClickField = styled.a`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  min-height: 100vh;
  content: '';
`

const CheckInOnClickListItem = styled.li`
  position: relative;
  padding: 12px;
  color: ${Colors.black};
  cursor: default;
  text-align: left;

  &:hover {
    background-color: #fff3f5;
  }
`
const CheckInOnClickListItemSmall = styled.span`
  margin-left: 3px;
  color: #999;
  font-size: 10px;
`

const CheckInOnClickButtonWrap = styled.div`
  margin-top: 24px;
  text-align: right;
`

const CheckInOnClickSpace = styled.div`
  position: relative;
  padding: 24px;

  &[data-type='calendar'] {
    padding: 2px 10px 24px;

    ${CheckInOnClickButtonWrap} {
      /* stylelint-disable-line */
      margin-top: 1px;
      padding-right: 18px;
    }
  }
`

const CheckInOnClickButton = styled.a`
  display: inline-block;
  padding: 8px;
  color: ${Colors.blue};
  position: relative;
  font-size: 14px;
  text-align: center;
  border: none;
  border-radius: 3px;
  border-bottom: transparent;
  cursor: pointer;

  &:not(:first-of-type) {
    margin-left: 12px;
  }

  &[data-button-type='active'] {
    width: 80px;
    color: ${Colors.white};
    font-weight: bold;
    background-color: ${Colors.blue};
    border-bottom: ${Colors.blueBdColor};
  }
`

const CampaignIcon = styled.i`
  color: ${Colors.blue};
  height: 14px;
  width: 14px;
  margin-right: 6px;
`

export default function SearchForm(props: PropTypes) {
  const {
    reserveOptions,
    amenities,
    sponsoredPromotions,
    autoCompleteItems,
    showAutoCompleteItems,
    showCheckInCalendar,
    showAmenityMore,
    dayPickerProps,
    singleDayPickerProps,
    events,
    fieldValues,
    momentLocale,
    eventTypes,
    capacities,
  } = props

  // react-datesの曜日をローカライズするための処理
  if (momentLocale !== moment.locale()) {
    moment.locale(momentLocale)
  }

  // アメニティの更に表示周りの処理
  let displayAmenities = Array.isArray(amenities) ? amenities : []
  if (!showAmenityMore) {
    displayAmenities = displayAmenities.slice(0, 6)
  }

  const priceTypes = [
    {
      id: 'hourly',
      label: i18n.t('page.search.priceType.hourly'),
      selected: true,
    },
    { id: 'daily', label: i18n.t('page.search.priceType.daily') },
  ]

  const hours = []
  for (let i = 0; i <= 23; i++) {
    hours.push({ id: i, label: `${i}:00` })
  }

  return (
    <SearchContent>
      <div className="search-side">
        <form>
          <ul>
            <li className="search-side__item" key="target">
              <p className="search-side__item-heading">
                <i className="icon icon-spm-target search-side__item-heading-icon" />
                {i18n.t('page.search.purpose')}
              </p>
              <SelectField
                label={i18n.t('page.search.labelTitle.selectPurpose')}
                options={eventTypes}
                value={fieldValues.eventType}
                iconType="event"
                onChange={(e: SyntheticInputEvent<*>) => {
                  events.eventSelected(e.target.value)
                }}
              />
            </li>
            <li className="search-side__item" key="area">
              <p className="search-side__item-heading">
                <i className="icon icon-spm-map_pin search-side__item-heading-icon" />
                {i18n.t('page.search.area')}
              </p>
              <TextField
                value={fieldValues.area}
                label={i18n.t('page.search.labelTitle.inputArea')}
                iconType="area"
                onChange={(e: SyntheticInputEvent<*>) =>
                  events.areaInput(e.target.value)
                }
                inputRef={props.areaInputRef}
              />
              {showAutoCompleteItems && (
                <CheckInOnClickWrapper data-type-width="fullWidth">
                  <ClickField onClick={e => props.events.areaClose(e)} />
                  <ul>
                    {Array.isArray(autoCompleteItems)
                      ? autoCompleteItems.map((item, index) => (
                          <a
                            href="#"
                            onClick={(e: SyntheticEvent<*>) => {
                              e.preventDefault()
                              events.areaSelected(item.original)
                            }}
                            key={`search_form_complete_item_${index}`}
                          >
                            <CheckInOnClickListItem
                              key={`check_in_list_item_${index}`}
                            >
                              {item.name}
                              <CheckInOnClickListItemSmall>
                                {item.detail}
                              </CheckInOnClickListItemSmall>
                            </CheckInOnClickListItem>
                          </a>
                        ))
                      : null}
                  </ul>
                </CheckInOnClickWrapper>
              )}
            </li>
            <li className="search-side__item" key="time">
              <p className="search-side__item-heading">
                <i className="icon icon-spm-reservation search-side__item-heading-icon" />
                {i18n.t('page.search.reservationType')}
              </p>
              <SelectField
                label={i18n.t('page.shared.unspecified')}
                options={priceTypes}
                value={fieldValues.priceType}
                iconType="event"
                onChange={(e: SyntheticInputEvent<*>) => {
                  events.priceTypeSelected(e.target.value)
                }}
              />
            </li>
            <li className="search-side__item" key="date">
              <p className="search-side__item-heading">
                <i className="icon icon-spm-calendar search-side__item-heading-icon" />
                {i18n.t('page.search.daytime')}
              </p>
              <div>
                <TextField
                  value={fieldValues.checkInOut}
                  label={i18n.t('page.search.useDate')}
                  iconType="checkIn"
                  onClick={events.checkInClicked}
                  calendar={true}
                  readOnly
                  textFieldType="calendar"
                />
              </div>
              {showCheckInCalendar &&
                fieldValues.priceType === 'daily' && (
                  <CheckInOnClickWrapper>
                    <ClickField onClick={e => props.events.checkInClose(e)} />
                    <CheckInOnClickSpace data-type="calendar">
                      <DayPickerRangeController {...dayPickerProps} />
                      <CheckInOnClickButtonWrap>
                        <CheckInOnClickButton onClick={events.checkInReset}>
                          {i18n.t('page.shared.unspecified')}
                        </CheckInOnClickButton>
                        <CheckInOnClickButton
                          data-button-type="active"
                          onClick={events.checkInApply}
                        >
                          {i18n.t('page.search.apply')}
                        </CheckInOnClickButton>
                      </CheckInOnClickButtonWrap>
                    </CheckInOnClickSpace>
                  </CheckInOnClickWrapper>
                )}
              {showCheckInCalendar &&
                fieldValues.priceType !== 'daily' && (
                  <CheckInOnClickWrapper>
                    <ClickField onClick={e => props.events.checkInClose(e)} />
                    <CheckInOnClickSpace data-type="calendar">
                      <DayPickerSingleDateController
                        {...singleDayPickerProps}
                      />
                      <CheckInOnClickButtonWrap>
                        <CheckInOnClickButton onClick={events.checkInReset}>
                          {i18n.t('page.shared.unspecified')}
                        </CheckInOnClickButton>
                        <CheckInOnClickButton
                          data-button-type="active"
                          onClick={events.checkInHourlyApply}
                        >
                          {i18n.t('page.search.apply')}
                        </CheckInOnClickButton>
                      </CheckInOnClickButtonWrap>
                    </CheckInOnClickSpace>
                  </CheckInOnClickWrapper>
                )}
              {fieldValues.priceType !== 'daily' && (
                <RangeSelectField
                  fromLabel={i18n.t('page.search.startedHour')}
                  fromOptions={hours}
                  fromOnChange={(e: SyntheticInputEvent<*>) => {
                    events.sartedHourSelected(e.target.value)
                  }}
                  fromValue={fieldValues.sartedHour}
                  toLabel={i18n.t('page.search.endedHour')}
                  toOptions={hours}
                  toOnChange={(e: SyntheticInputEvent<*>) => {
                    events.endedHourSelected(e.target.value)
                  }}
                  toValue={fieldValues.endedHour}
                />
              )}
            </li>

            <li className="search-side__item" key="people">
              <p className="search-side__item-heading">
                <i className="icon icon-spm-user search-side__item-heading-icon" />
                {i18n.t('page.search.capacity')}
              </p>
              <RangeSelectField
                fromLabel={i18n.t('page.search.minCapacity')}
                fromOptions={capacities}
                fromOnChange={(e: SyntheticInputEvent<*>) => {
                  events.minCapacitySelected(e.target.value)
                }}
                fromValue={fieldValues.minCapacity}
                toLabel={i18n.t('page.search.maxCapacity')}
                toOptions={capacities}
                toOnChange={(e: SyntheticInputEvent<*>) => {
                  events.maxCapacitySelected(e.target.value)
                }}
                toValue={fieldValues.maxCapacity}
              />
            </li>
          </ul>
        </form>
        <FilterLink
          href="#"
          onClick={(e: SyntheticInputEvent<*>) => {
            events.searchButtonClicked(e)
          }}
        >
          {i18n.t('page.search.narrowSearch')}
        </FilterLink>
      </div>
      <Section>
        <Body>
          <SideContentTitle>
            {i18n.t('page.search.reserveOptions')}
          </SideContentTitle>
          <BodyContent>
            <ul className="search-reserve-options">
              {reserveOptions.map(item => (
                <li
                  className="check-box-list-item_one_liner"
                  key={`reserve_option_item_${item.id}`}
                >
                  <CheckboxWrap
                    item={item}
                    onChange={(e: SyntheticInputEvent<*>) =>
                      events.reserveOptionValueChanged(item, e.target.checked)
                    }
                    checked={item.checked}
                  />
                </li>
              ))}
            </ul>
          </BodyContent>
        </Body>
        {sponsoredPromotions.length > 0 && (
          <Body>
            <SideContentTitle>
              <CampaignIcon className="icon icon-spm-present" />
              {i18n.t('page.search.sponsoredPromotions')}
            </SideContentTitle>
            <BodyContent>
              <CheckboxList>
                {sponsoredPromotions.map((item, index) => (
                  <CheckboxListItem oneLiner key={index}>
                    <CheckboxWrap
                      item={item}
                      onChange={(e: SyntheticInputEvent<*>) =>
                        events.sponsoredPromotionsValueChanged(
                          item,
                          e.target.checked,
                        )
                      }
                      checked={item.checked}
                    />
                  </CheckboxListItem>
                ))}
              </CheckboxList>
            </BodyContent>
          </Body>
        )}
        <Body>
          <SideContentTitle>{i18n.t('page.search.budget')}</SideContentTitle>
          <RangeBodyContent>
            <Range
              {...props.budgetRangeProps}
              railStyle={railStyle}
              trackStyle={[
                props.isBudgetUnspecified ? trackUnspecifiedStyle : trackStyle,
              ]}
              handleStyle={[
                props.isBudgetUnspecified
                  ? handleUnspecifiedStyle
                  : handleStyle,
                props.isBudgetUnspecified
                  ? handleUnspecifiedStyle
                  : handleStyle,
              ]}
            />
            <div className="clearfix slider-input-container">
              <SlideInputWrapper data-layout-type="left">
                <input
                  className="search-slider-input"
                  value={fieldValues.minBudget}
                  placeholder={i18n.t('page.search.budgetStartUnlimited')}
                  type="number"
                  onChange={(e: SyntheticInputEvent<*>) => {
                    events.minBudgetInput(e.target.value)
                  }}
                  onBlur={() => events.budgetApply()}
                />
              </SlideInputWrapper>
              <div className="slider-input-text">
                {i18n.t('page.search.budgetUnit')}
              </div>
              <div className="slide-tilde">〜</div>
              <SlideInputWrapper data-layout-type="right">
                <input
                  className="search-slider-input"
                  value={fieldValues.maxBudget}
                  placeholder={i18n.t('page.search.budgetEndUnlimited')}
                  type="number"
                  onChange={(e: SyntheticInputEvent<*>) => {
                    events.maxBudgetInput(e.target.value)
                  }}
                  onBlur={() => events.budgetApply()}
                />
              </SlideInputWrapper>
              <div className="slider-input-text">
                {i18n.t('page.search.budgetUnit')}
              </div>
            </div>
          </RangeBodyContent>
        </Body>
        <Body>
          <SideContentTitle>{i18n.t('page.search.amenity')}</SideContentTitle>
          <BodyContent>
            <CheckboxList>
              {displayAmenities.map((item, index) => (
                <CheckboxListItem key={`checkbox_list_item_${index}`}>
                  <CheckboxWrap
                    item={item}
                    onChange={(e: SyntheticInputEvent<*>) =>
                      events.amenityValueChanged(item, e.target.checked)
                    }
                    checked={item.checked}
                  />
                </CheckboxListItem>
              ))}
            </CheckboxList>
            {!showAmenityMore && (
              <a
                href="#"
                onClick={(e: SyntheticInputEvent<*>) => {
                  e.preventDefault()
                  events.showMoreAmenity(true)
                }}
              >
                <p className="search-more-read">
                  {i18n.t('page.search.displayMore')}
                </p>
              </a>
            )}
            {showAmenityMore && (
              <a
                href="#"
                onClick={(e: SyntheticInputEvent<*>) => {
                  e.preventDefault()
                  events.showMoreAmenity(false)
                }}
              >
                <p className="search-more-read" data-display-status="close">
                  {i18n.t('page.search.close')}
                </p>
              </a>
            )}
          </BodyContent>
        </Body>
        <Body last>
          <SideContentTitle>{i18n.t('page.search.keyword')}</SideContentTitle>
          <TextField
            value={fieldValues.keyword}
            label={i18n.t('page.search.keywordPlaceholder')}
            onChange={(e: SyntheticInputEvent<*>) =>
              events.keywordInput(e.target.value)
            }
          />
        </Body>
        <FilterLink
          href="#"
          onClick={(e: SyntheticInputEvent<*>) => {
            events.searchButtonClicked(e)
          }}
        >
          {i18n.t('page.search.narrowSearch')}
        </FilterLink>
        <SearchButtonContainerSpSpace />
      </Section>
      <div className="search-button-container-sp">
        <SearchButtonWrapper>
          <SearchButton
            data-button-type="cancel"
            onClick={(e: SyntheticInputEvent<*>) => {
              e.preventDefault()
              events.cancelButtonClicked(e)
            }}
          >
            {i18n.t('page.search.cancel')}
          </SearchButton>
        </SearchButtonWrapper>
        <SearchButtonWrapper>
          <SearchButton
            data-button-type="active"
            onClick={(e: SyntheticInputEvent<*>) => {
              events.searchButtonClicked(e)
            }}
          >
            {i18n.t('page.search.search')}
          </SearchButton>
        </SearchButtonWrapper>
      </div>
    </SearchContent>
  )
}



// WEBPACK FOOTER //
// ./src/components/organisms/SearchForm/index.jsx